import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
	PencilIcon,
	CalendarDaysIcon,
	ClockIcon,
	DocumentTextIcon,
	BuildingOfficeIcon,
	ChevronRightIcon as ChevronRightOutlineIcon,
	ChevronDownIcon,
	ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { PlayIcon } from '@heroicons/react/24/solid';

import {
	StackableModal,
	List,
	Badge,
	StatusBadge,
	TextStack,
} from '../components/nextgen';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

import { getPMSchedule, generateWorkOrderNow } from '../api/pmSchedules';

const MODAL_NAME = 'pmScheduleOverview';

const FREQUENCY_LABELS = {
	daily: 'Daily',
	weekly: 'Weekly',
	biweekly: 'Bi-weekly',
	monthly: 'Monthly',
	quarterly: 'Quarterly',
	biyearly: 'Bi-yearly',
	yearly: 'Yearly',
	custom: 'Custom',
};

const STATUS_COLORS = {
	pending: 'yellow',
	scheduled: 'yellow',
	in_progress: 'blue',
	completed: 'green',
	cancelled: 'gray',
};

const PMScheduleOverview = () => {
	const navigate = useNavigate();
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openEditPMSchedule } = useModal('editPMSchedule');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');
	const { setOpen: openContractOverview } = useModal('contractOverview');

	const [workOrderPage, setWorkOrderPage] = useState(1);
	const [showAssets, setShowAssets] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const WORK_ORDERS_PER_PAGE = 5;

	const {
		value: schedule,
		execute: fetchSchedule,
		status: loadingStatus,
	} = useAsync(getPMSchedule, { immediate: false });

	useEffect(() => {
		if (open?.scheduleId) {
			fetchSchedule({ id: open.scheduleId });
			setWorkOrderPage(1);
		}
	}, [open?.scheduleId]);

	const handleClose = () => {
		setClose();
		if (typeof open?.onClose === 'function') {
			open.onClose();
		}
	};

	const handleEdit = () => {
		openEditPMSchedule({
			schedule,
			onSuccess: () => {
				fetchSchedule({ id: open.scheduleId });
				if (typeof open?.onSuccess === 'function') {
					open.onSuccess();
				}
			},
			onDeleted: () => {
				if (typeof open?.onSuccess === 'function') {
					open.onSuccess();
				}
				handleClose();
			},
		});
	};

	const handleWorkOrderClick = (workOrder) => {
		openWorkOrderOverview({
			id: workOrder.id,
		});
	};

	const handleAssetClick = (asset) => {
		setClose();
		navigate(`/assets/${asset.id}/overview`);
	};

	const handleGenerateNow = async () => {
		if (!schedule?.id || isGenerating) return;

		try {
			setIsGenerating(true);
			const result = await generateWorkOrderNow({ id: schedule.id });
			// Refresh the schedule to show the new work order
			await fetchSchedule({ id: schedule.id });
			// Open the newly created work order
			if (result?.workOrder?.id) {
				openWorkOrderOverview({ id: result.workOrder.id });
			}
			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
		} catch (error) {
			console.error('Failed to generate work order:', error);
		} finally {
			setIsGenerating(false);
		}
	};

	const isLoading = loadingStatus === 'pending';

	const workOrders = schedule?.workOrders || [];

	// Separate active work orders from past work orders
	const activeWorkOrders = workOrders
		.filter(
			(wo) =>
				wo.status === 'pending' ||
				wo.status === 'scheduled' ||
				wo.status === 'in_progress',
		)
		.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
	const pastWorkOrders = workOrders
		.filter((wo) => wo.status === 'completed' || wo.status === 'cancelled')
		.sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));

	// Pagination for past work orders only
	const totalPages = Math.ceil(pastWorkOrders.length / WORK_ORDERS_PER_PAGE);
	const paginatedWorkOrders = pastWorkOrders.slice(
		(workOrderPage - 1) * WORK_ORDERS_PER_PAGE,
		workOrderPage * WORK_ORDERS_PER_PAGE,
	);

	// Combine direct assets and contract assets
	const allAssets = useMemo(() => {
		const directAssets = schedule?.directAssets || [];
		const contractAssets = schedule?.contract?.assets || [];
		// Dedupe by id in case same asset appears in both
		const assetMap = new Map();
		[...directAssets, ...contractAssets].forEach((asset) => {
			if (!assetMap.has(asset.id)) {
				assetMap.set(asset.id, asset);
			}
		});
		return Array.from(assetMap.values());
	}, [schedule]);

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title={schedule?.name || 'PM Schedule'}
			size="2xl"
		>
			{isLoading ? (
				<div className="flex items-center justify-center py-12">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
				</div>
			) : schedule ? (
				<div className="pt-4 pb-6">
					{/* Next Due Card with Generate Now */}
					<div className="mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-lg p-4">
						<div className="flex items-center justify-between">
							<div>
								<div className="flex items-center gap-2 text-indigo-600 text-sm font-medium mb-1">
									<CalendarDaysIcon className="w-4 h-4" />
									Next Scheduled PM
								</div>
								<div className="text-2xl font-bold text-gray-900">
									{schedule.nextDueDate
										? moment(schedule.nextDueDate).format('MMM D, YYYY')
										: 'Not scheduled'}
								</div>
								{schedule.nextDueDate && (
									<div className="text-sm text-gray-500 mt-1">
										{moment(schedule.nextDueDate).fromNow()}
									</div>
								)}
							</div>
							{schedule.nextDueDate && schedule.active && (
								<button
									type="button"
									onClick={handleGenerateNow}
									disabled={isGenerating}
									className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
								>
									{isGenerating ? (
										<>
											<div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
											Generating...
										</>
									) : (
										<>
											<PlayIcon className="w-4 h-4" />
											Generate Now
										</>
									)}
								</button>
							)}
						</div>
						<p className="text-xs text-gray-500 mt-3">
							Work orders are automatically generated{' '}
							{schedule.generateDaysBefore} days before the due date. Use
							"Generate Now" to create a work order immediately.
						</p>
					</div>

					{/* Active Work Orders */}
					{activeWorkOrders.length > 0 && (
						<div className="mb-6">
							<h4 className="text-sm font-medium text-gray-900 mb-3">
								Active Work Orders ({activeWorkOrders.length})
							</h4>
							<div className="space-y-3">
								{activeWorkOrders.map((wo) => (
									<div
										key={wo.id}
										className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 cursor-pointer hover:bg-indigo-100 transition-colors"
										onClick={() => handleWorkOrderClick(wo)}
									>
										<div className="flex items-center justify-between mb-2">
											<span className="font-medium text-indigo-700 text-sm">
												{wo.workOrderCode}
											</span>
											<Badge preset="status" value={wo.status} />
										</div>
										<p className="text-sm text-gray-900 mb-2">{wo.title}</p>
										<div className="text-sm text-gray-500">
											Due: {moment(wo.dueDate).format('MMM D, YYYY')}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					<div className="space-y-4 pb-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="col-span-2">
								<label className="text-xs text-gray-500 uppercase tracking-wide">
									Description
								</label>
								<div className="mt-1 text-sm text-gray-900">
									{schedule?.description || 'No description provided'}
								</div>
							</div>
							<div>
								<label className="text-xs text-gray-500 uppercase tracking-wide">
									Status
								</label>
								<div className="mt-1 text-sm text-gray-900">
									<div className="flex items-center gap-3">
										<StatusBadge
											value={schedule.active ? 'active' : 'inactive'}
										/>
									</div>
								</div>
							</div>
							<div>
								<label className="text-xs text-gray-500 uppercase tracking-wide">
									Frequency
								</label>
								<div className="mt-1 text-sm text-gray-900">
									{FREQUENCY_LABELS[schedule.occurrences] ||
										schedule.occurrences}
								</div>
							</div>

							<div>
								<label className="text-xs text-gray-500 uppercase tracking-wide">
									Category
								</label>
								<div className="mt-1 text-sm text-gray-900">
									{schedule.category?.name || 'None'}
								</div>
							</div>
							<div>
								<label className="text-xs text-gray-500 uppercase tracking-wide">
									Start Date
								</label>
								<div className="mt-1 text-sm text-gray-900">
									{schedule.startDate
										? moment(schedule.startDate).format('MMM D, YYYY')
										: 'Not set'}
								</div>
							</div>
							<div className="col-span-2">
								<label className="text-xs text-gray-500 uppercase tracking-wide">
									SOP Template
								</label>
								<div className="mt-1 text-sm text-gray-900">
									{schedule.sop ? (
										<span>
											{schedule.sop.name}
											{schedule.sop.steps?.length > 0 && (
												<span className="text-gray-500 ml-1">
													({schedule.sop.steps.length} step
													{schedule.sop.steps.length !== 1 ? 's' : ''})
												</span>
											)}
										</span>
									) : (
										'None'
									)}
								</div>
							</div>

							{/* Contract Card */}
							{schedule.contract ? (
								<div
									className=" border-gray-200 rounded-lg p-2 mb-6 cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors"
									onClick={() =>
										openContractOverview({ id: schedule.contract.id })
									}
								>
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-3">
											<div className="p-2 bg-gray-100 rounded-lg">
												<DocumentTextIcon className="w-5 h-5 text-gray-600" />
											</div>
											<div>
												<TextStack
													primary={schedule.contract.name}
													secondary={
														<>
															{schedule.contract.customer?.name && (
																<span className="text-xs text-gray-500">
																	{schedule.contract.customer.name}
																</span>
															)}
															{schedule.contract.assets?.length > 0 && (
																<span className="text-xs text-gray-500">
																	{schedule.contract.assets.length} asset
																	{schedule.contract.assets.length !== 1
																		? 's'
																		: ''}
																</span>
															)}
														</>
													}
												/>
											</div>
										</div>
										<ChevronRightOutlineIcon className="w-5 h-5 text-gray-400" />
									</div>
								</div>
							) : (
								<div className="border border-dashed border-gray-200 rounded-lg p-4 mb-6">
									<div className="flex items-center gap-3">
										<div className="p-2 bg-gray-50 rounded-lg">
											<BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
										</div>
										<p className="text-sm text-gray-500">No contract linked</p>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Quick Stats */}
					<div className="grid grid-cols-2 gap-4 mb-6">
						<div className="bg-gray-50 rounded-lg p-4">
							<div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
								<ClockIcon className="w-4 h-4" />
								Lead Time
							</div>
							<div className="text-lg font-semibold text-gray-900">
								{schedule.generateDaysBefore} days
							</div>
						</div>
						<div className="bg-gray-50 rounded-lg p-4">
							<div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
								<DocumentTextIcon className="w-4 h-4" />
								Total Work Orders
							</div>
							<div className="text-lg font-semibold text-gray-900">
								{workOrders.length}
							</div>
						</div>
					</div>

					{/* Past Work Orders Section */}
					<div className="mb-6">
						<h4 className="text-sm font-medium text-gray-900 mb-3">
							Past Work Orders ({pastWorkOrders.length})
						</h4>
						{pastWorkOrders.length > 0 ? (
							<>
								<List
									data={paginatedWorkOrders}
									onItemClick={handleWorkOrderClick}
									compact
									renderItem={(wo) => (
										<div className="flex items-center justify-between">
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2">
													<span className="font-medium text-indigo-600 text-sm">
														{wo.workOrderCode}
													</span>
													<Badge color={STATUS_COLORS[wo.status] || 'gray'}>
														{wo.status?.replace('_', ' ')}
													</Badge>
												</div>
												<p className="text-sm text-gray-900 truncate mt-1">
													{wo.title}
												</p>
											</div>
											<div className="text-sm text-gray-500 ml-4">
												{wo.dueDate
													? moment(wo.dueDate).format('MMM D, YYYY')
													: '-'}
											</div>
										</div>
									)}
								/>
								{totalPages > 1 && (
									<div className="flex items-center justify-between pt-2">
										<p className="text-sm text-gray-500">
											Showing {(workOrderPage - 1) * WORK_ORDERS_PER_PAGE + 1}-
											{Math.min(
												workOrderPage * WORK_ORDERS_PER_PAGE,
												pastWorkOrders.length,
											)}{' '}
											of {pastWorkOrders.length}
										</p>
										<div className="flex items-center gap-1">
											<button
												onClick={() =>
													setWorkOrderPage((p) => Math.max(1, p - 1))
												}
												disabled={workOrderPage === 1}
												className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
											>
												<ChevronLeftIcon className="w-5 h-5 text-gray-600" />
											</button>
											<span className="text-sm text-gray-600 px-2">
												{workOrderPage} / {totalPages}
											</span>
											<button
												onClick={() =>
													setWorkOrderPage((p) => Math.min(totalPages, p + 1))
												}
												disabled={workOrderPage === totalPages}
												className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
											>
												<ChevronRightIcon className="w-5 h-5 text-gray-600" />
											</button>
										</div>
									</div>
								)}
							</>
						) : (
							<div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
								<DocumentTextIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
								<p className="text-sm">No past work orders yet</p>
							</div>
						)}
					</div>

					{/* Assets Section - Collapsible */}
					{allAssets.length > 0 && (
						<div className="mb-6">
							<button
								type="button"
								onClick={() => setShowAssets(!showAssets)}
								className="flex items-center justify-between w-full text-left"
							>
								<h4 className="text-sm font-medium text-gray-900">
									Assets ({allAssets.length})
								</h4>
								{showAssets ? (
									<ChevronUpIcon className="w-5 h-5 text-gray-400" />
								) : (
									<ChevronDownIcon className="w-5 h-5 text-gray-400" />
								)}
							</button>
							{showAssets && (
								<div className="mt-3 space-y-2">
									{allAssets.map((asset) => (
										<div
											key={asset.id}
											className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
											onClick={() => handleAssetClick(asset)}
										>
											<div className="flex-1 min-w-0">
												<p className="text-sm font-medium text-gray-900 truncate">
													{[
														asset.manufacturer?.name || asset.manufacturerName,
														asset.model?.name || asset.modelName,
													]
														.filter(Boolean)
														.join(' ') || 'Unnamed Asset'}
												</p>
												<p className="text-xs text-gray-500 truncate">
													{asset.location?.name ||
														asset.locationName ||
														'No Location'}{' '}
													· SN: {asset.serialNumber || 'N/A'}
												</p>
											</div>
											<ChevronRightOutlineIcon className="w-4 h-4 text-gray-400" />
										</div>
									))}
								</div>
							)}
						</div>
					)}

					<button
						onClick={handleEdit}
						className="mt-6 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					>
						<PencilIcon className="w-4 h-4" />
						Edit
					</button>
				</div>
			) : (
				<div className="text-center py-12 text-gray-500">
					Schedule not found
				</div>
			)}
		</StackableModal>
	);
};

export default PMScheduleOverview;
